import * as React from 'react';
import { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Chip,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormGroup,
    FormControlLabel,
    Checkbox,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DownloadIcon from '@mui/icons-material/Download';
import DataService from '../dataService';
import BytesDisplay from '../../../../components/BytesDisplay';

const dataService = new DataService();

// Helper to convert month number to name
const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Helper to convert Julian day to month/day
function julianToDate(year, julianDay) {
    const date = new Date(year, 0);
    date.setDate(julianDay);
    return date;
}

function DataCalendar({ onDaySelect, onRangeSelect }) {
    const [calendar, setCalendar] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectionMode, setSelectionMode] = useState('single'); // 'single' or 'range'
    const [rangeStart, setRangeStart] = useState(null);
    const [rangeEnd, setRangeEnd] = useState(null);

    useEffect(() => {
        loadCalendar();
    }, []);

    function loadCalendar() {
        setLoading(true);
        dataService.getDataCalendar().then(data => {
            setCalendar(data);
            // Set selected year to most recent year with data
            if (Object.keys(data).length > 0) {
                const years = Object.keys(data).map(Number).sort((a, b) => b - a);
                setSelectedYear(years[0]);
            }
            setLoading(false);
        }).catch(err => {
            console.error('Failed to load calendar:', err);
            setLoading(false);
        });
    }

    function dateToJulian(year, month, day) {
        const date = new Date(year, month - 1, day);
        const start = new Date(year, 0, 0);
        const diff = date - start;
        const oneDay = 1000 * 60 * 60 * 24;
        return Math.floor(diff / oneDay);
    }

    function handleDayClick(year, month, day) {
        const julianDay = dateToJulian(year, month, day);

        if (selectionMode === 'single') {
            onDaySelect(year, julianDay);
        } else {
            // Range selection mode
            if (!rangeStart || (rangeStart && rangeEnd)) {
                // Start new range
                setRangeStart({ year, month, day, julianDay });
                setRangeEnd(null);
            } else {
                // Complete the range
                const endJulian = julianDay;
                if (endJulian >= rangeStart.julianDay) {
                    setRangeEnd({ year, month, day, julianDay: endJulian });
                } else {
                    // Swap if end is before start
                    setRangeEnd(rangeStart);
                    setRangeStart({ year, month, day, julianDay: endJulian });
                }
            }
        }
    }

    function handleDownloadRange() {
        if (rangeStart && rangeEnd && onRangeSelect) {
            onRangeSelect(rangeStart.year, rangeStart.julianDay, rangeEnd.year, rangeEnd.julianDay);
            // Reset range
            setRangeStart(null);
            setRangeEnd(null);
        }
    }

    function isDateInRange(year, month, day) {
        if (!rangeStart || !rangeEnd) return false;
        const julianDay = dateToJulian(year, month, day);
        return julianDay >= rangeStart.julianDay && julianDay <= rangeEnd.julianDay;
    }

    function isDateRangeStart(year, month, day) {
        if (!rangeStart) return false;
        return rangeStart.year === year && rangeStart.month === month && rangeStart.day === day;
    }

    function isDateRangeEnd(year, month, day) {
        if (!rangeEnd) return false;
        return rangeEnd.year === year && rangeEnd.month === month && rangeEnd.day === day;
    }

    function renderMonth(year, monthNum) {
        const monthData = calendar[year]?.[monthNum] || [];
        const monthIndex = parseInt(monthNum) - 1;
        const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
        const firstDayOfWeek = new Date(year, monthIndex, 1).getDay();

        const days = [];

        // Empty cells for days before month starts
        for (let i = 0; i < firstDayOfWeek; i++) {
            days.push(<Box key={`empty-${i}`} sx={{ width: 32, height: 32, m: 0.25 }} />);
        }

        // Actual days
        for (let day = 1; day <= daysInMonth; day++) {
            const hasData = monthData.includes(day);
            const inRange = selectionMode === 'range' && isDateInRange(year, monthNum, day);
            const isStart = selectionMode === 'range' && isDateRangeStart(year, monthNum, day);
            const isEnd = selectionMode === 'range' && isDateRangeEnd(year, monthNum, day);

            let bgcolor = 'grey.200';
            let color = 'grey.400';
            if (hasData) {
                if (isStart || isEnd) {
                    bgcolor = 'primary.dark';
                    color = 'white';
                } else if (inRange) {
                    bgcolor = 'primary.light';
                    color = 'primary.contrastText';
                } else {
                    bgcolor = 'primary.main';
                    color = 'white';
                }
            }

            days.push(
                <Tooltip key={day} title={hasData ? `${monthNames[monthIndex]} ${day}, ${year}` : 'No data'}>
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            m: 0.25,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 1,
                            cursor: hasData ? 'pointer' : 'default',
                            bgcolor,
                            color,
                            fontSize: '0.875rem',
                            fontWeight: hasData ? 'bold' : 'normal',
                            border: (isStart || isEnd) ? '2px solid' : 'none',
                            borderColor: (isStart || isEnd) ? 'primary.dark' : 'transparent',
                            '&:hover': hasData ? {
                                bgcolor: 'primary.dark',
                                transform: 'scale(1.1)',
                                transition: 'all 0.2s'
                            } : {}
                        }}
                        onClick={() => hasData && handleDayClick(year, monthNum, day)}
                    >
                        {day}
                    </Box>
                </Tooltip>
            );
        }

        return (
            <Card key={monthNum} sx={{ minWidth: 275, m: 1 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {monthNames[monthIndex]}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                            <Box
                                key={i}
                                sx={{
                                    width: 32,
                                    height: 24,
                                    m: 0.25,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.75rem',
                                    fontWeight: 'bold',
                                    color: 'grey.600'
                                }}
                            >
                                {day}
                            </Box>
                        ))}
                        {days}
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (loading) {
        return <LinearProgress />;
    }

    const years = Object.keys(calendar).map(Number).sort((a, b) => b - a);

    if (years.length === 0) {
        return (
            <Alert severity="info">
                No data available. Start the RINEX recording service to begin collecting data.
            </Alert>
        );
    }

    return (
        <Box>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <CalendarTodayIcon color="primary" />
                <Typography variant="h5">Data Availability Calendar</Typography>
            </Box>

            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <ToggleButtonGroup
                    value={selectedYear}
                    exclusive
                    onChange={(e, newYear) => newYear && setSelectedYear(newYear)}
                    aria-label="year selection"
                >
                    {years.map(year => (
                        <ToggleButton key={year} value={year}>
                            {year}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <ToggleButtonGroup
                        value={selectionMode}
                        exclusive
                        onChange={(e, newMode) => {
                            if (newMode) {
                                setSelectionMode(newMode);
                                setRangeStart(null);
                                setRangeEnd(null);
                            }
                        }}
                        size="small"
                    >
                        <ToggleButton value="single">Single Day</ToggleButton>
                        <ToggleButton value="range">Date Range</ToggleButton>
                    </ToggleButtonGroup>

                    {selectionMode === 'range' && rangeStart && rangeEnd && (
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadRange}
                        >
                            Download Range
                        </Button>
                    )}
                </Box>
            </Box>

            {selectionMode === 'range' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {!rangeStart ? (
                        'Click on a day to start selecting a date range'
                    ) : !rangeEnd ? (
                        `Range start: ${monthNames[rangeStart.month - 1]} ${rangeStart.day}, ${rangeStart.year}. Click another day to complete the range.`
                    ) : (
                        `Selected: ${monthNames[rangeStart.month - 1]} ${rangeStart.day} - ${monthNames[rangeEnd.month - 1]} ${rangeEnd.day}, ${rangeStart.year}`
                    )}
                </Alert>
            )}

            <Grid container spacing={1}>
                {Object.keys(calendar[selectedYear] || {}).sort((a, b) => a - b).map(monthNum => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={monthNum}>
                        {renderMonth(selectedYear, monthNum)}
                    </Grid>
                ))}
            </Grid>

            <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 20, height: 20, bgcolor: 'primary.main', borderRadius: 1 }} />
                    <Typography variant="body2">Has data</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 20, height: 20, bgcolor: 'grey.200', borderRadius: 1 }} />
                    <Typography variant="body2">No data</Typography>
                </Box>
            </Box>
        </Box>
    );
}

function DayDetailView({ year, julianDay, onClose, onDownload }) {
    const [dayData, setDayData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (year && julianDay) {
            loadDayDetail();
        }
    }, [year, julianDay]);

    function loadDayDetail() {
        setLoading(true);
        dataService.getDayDetail(year, julianDay).then(response => {
            setDayData(response.hours);
            setLoading(false);
        }).catch(err => {
            console.error('Failed to load day detail:', err);
            setLoading(false);
        });
    }

    function handleDownloadFile(file, hour) {
        const fileWithMeta = { ...file, year, day: julianDay };
        onDownload([fileWithMeta]);
    }

    function handleDownloadAll() {
        // Collect all files from all hours
        const allFiles = [];
        dayData.forEach(hourData => {
            hourData.files.forEach(file => {
                allFiles.push({ ...file, year, day: julianDay });
            });
        });
        onDownload(allFiles);
    }

    const date = julianToDate(year, julianDay);
    const dateStr = `${monthNames[date.getMonth()]} ${date.getDate()}, ${year}`;

    if (loading) {
        return (
            <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
                <DialogContent>
                    <LinearProgress />
                </DialogContent>
            </Dialog>
        );
    }

    // Get only hours with files
    const hoursWithFiles = dayData?.filter(h => h.files.length > 0) || [];
    const totalBytes = hoursWithFiles.reduce((sum, hour) => sum + hour.total_bytes, 0);
    const totalFiles = hoursWithFiles.reduce((sum, hour) => sum + hour.files.length, 0);

    return (
        <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {dateStr} (Day {julianDay})
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        {totalFiles} file{totalFiles !== 1 ? 's' : ''}, <BytesDisplay bytes={totalBytes} />
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadAll}
                        disabled={totalFiles === 0}
                    >
                        Download Daily File
                    </Button>
                </Box>

                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Hour (UTC)</TableCell>
                                <TableCell>Filename</TableCell>
                                <TableCell align="right">Size</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {hoursWithFiles.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                            No data available for this day
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                hoursWithFiles.map((hourData) => (
                                    hourData.files.map(file => (
                                        <TableRow key={file.filename} hover>
                                            <TableCell>
                                                {String(hourData.hour).padStart(2, '0')}:00
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontFamily="monospace">
                                                    {file.filename}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <BytesDisplay bytes={file.size} />
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleDownloadFile(file, hourData.hour)}
                                                >
                                                    <DownloadIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

function DownloadModal({ files, onClose }) {
    const [format, setFormat] = useState('bin');
    const [interval, setInterval] = useState(30);
    const [constellations, setConstellations] = useState(['GPS', 'GLONASS', 'Galileo', 'BeiDou']);
    const [merge, setMerge] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState(null);

    // Generate smart default filename
    const generateDefaultFilename = () => {
        if (files.length === 1) {
            // Single file: use its base name without extension
            return files[0].filename.rsplit('.', 1)[0] || files[0].filename.replace(/\..*$/, '');
        } else if (merge) {
            // Multiple files merged: describe the date range
            const firstFile = files[0];
            const lastFile = files[files.length - 1];

            // Extract station ID from first filename (first 4 chars)
            const stationId = firstFile.filename.substring(0, 4);

            if (firstFile.year === lastFile.year && firstFile.day === lastFile.day) {
                // Same day: AAAA_YYYY_DDD
                return `${stationId}_${firstFile.year}_${String(firstFile.day).padStart(3, '0')}`;
            } else {
                // Multiple days: AAAA_YYYY_DDD-DDD
                return `${stationId}_${firstFile.year}_${String(firstFile.day).padStart(3, '0')}-${String(lastFile.day).padStart(3, '0')}`;
            }
        } else {
            // Multiple files not merged
            return `rinex_data_${files[0].year}_${files[0].day}`;
        }
    };

    const [customFilename, setCustomFilename] = useState(generateDefaultFilename());

    function handleDownload() {
        setDownloading(true);
        setError(null);

        dataService.convertAndDownload(files, format, interval, constellations, merge, customFilename)
            .then(blob => {
                // Create download link
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;

                // Add proper file extension based on format and year
                let filename = customFilename || 'download';
                if (format === 'rinex') {
                    // Get year from first file to determine RINEX extension
                    const year = files[0]?.year || new Date().getFullYear();
                    const yearSuffix = String(year).slice(-2); // Last 2 digits
                    filename = `${filename}.${yearSuffix}o`;
                } else if (format === 'bin') {
                    filename = `${filename}.bin`;
                }
                link.download = filename;

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                setDownloading(false);
                onClose();
            })
            .catch(err => {
                console.error('Download failed:', err);
                setError('Download failed. Please try again.');
                setDownloading(false);
            });
    }

    function toggleConstellation(const_name) {
        if (constellations.includes(const_name)) {
            setConstellations(constellations.filter(c => c !== const_name));
        } else {
            setConstellations([...constellations, const_name]);
        }
    }

    const totalSize = files.reduce((sum, f) => sum + f.size, 0);

    return (
        <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Download Options</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        {files.length} file{files.length > 1 ? 's' : ''} selected, <BytesDisplay bytes={totalSize} />
                    </Typography>

                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>Filename (without extension)</Typography>
                        <TextField
                            value={customFilename}
                            onChange={(e) => setCustomFilename(e.target.value)}
                            fullWidth
                            size="small"
                            placeholder="Enter filename"
                            helperText="Extension will be added automatically based on format"
                        />
                    </Box>

                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>Format</Typography>
                        <ToggleButtonGroup
                            value={format}
                            exclusive
                            onChange={(e, newFormat) => newFormat && setFormat(newFormat)}
                            fullWidth
                        >
                            <ToggleButton value="bin">Binary</ToggleButton>
                            <ToggleButton value="rinex">RINEX</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {format === 'rinex' && (
                        <>
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>Interval (seconds)</Typography>
                                <TextField
                                    type="number"
                                    value={interval}
                                    onChange={(e) => setInterval(parseInt(e.target.value))}
                                    fullWidth
                                    size="small"
                                    inputProps={{ min: 1, max: 3600 }}
                                />
                            </Box>

                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>Constellations</Typography>
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={constellations.includes('GPS')}
                                                onChange={() => toggleConstellation('GPS')}
                                            />
                                        }
                                        label="GPS"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={constellations.includes('GLONASS')}
                                                onChange={() => toggleConstellation('GLONASS')}
                                            />
                                        }
                                        label="GLONASS"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={constellations.includes('Galileo')}
                                                onChange={() => toggleConstellation('Galileo')}
                                            />
                                        }
                                        label="Galileo"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={constellations.includes('BeiDou')}
                                                onChange={() => toggleConstellation('BeiDou')}
                                            />
                                        }
                                        label="BeiDou"
                                    />
                                </FormGroup>
                            </Box>
                        </>
                    )}

                    {files.length > 1 && (
                        <Box sx={{ mt: 3 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={merge}
                                        onChange={(e) => setMerge(e.target.checked)}
                                    />
                                }
                                label="Merge all files into single file"
                            />
                        </Box>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {downloading && <LinearProgress sx={{ mt: 2 }} />}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={downloading}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleDownload}
                    disabled={downloading || (format === 'rinex' && constellations.length === 0)}
                    startIcon={<DownloadIcon />}
                >
                    {downloading ? 'Downloading...' : 'Download'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default function DataBrowser() {
    const [selectedDay, setSelectedDay] = useState(null);
    const [downloadFiles, setDownloadFiles] = useState(null);

    function handleDaySelect(year, julianDay) {
        setSelectedDay({ year, julianDay });
    }

    function handleRangeSelect(startYear, startJulian, endYear, endJulian) {
        // Fetch all files in the range from the backend
        dataService.getRangeFiles(startYear, startJulian, endYear, endJulian)
            .then(response => {
                if (response.files && response.files.length > 0) {
                    setDownloadFiles(response.files);
                } else {
                    alert('No files found in the selected date range');
                }
            })
            .catch(err => {
                console.error('Failed to fetch range files:', err);
                alert('Failed to fetch files for the selected range');
            });
    }

    function handleDownload(files) {
        setDownloadFiles(files);
        setSelectedDay(null);
    }

    return (
        <Box>
            <DataCalendar onDaySelect={handleDaySelect} onRangeSelect={handleRangeSelect} />

            {selectedDay && (
                <DayDetailView
                    year={selectedDay.year}
                    julianDay={selectedDay.julianDay}
                    onClose={() => setSelectedDay(null)}
                    onDownload={handleDownload}
                />
            )}

            {downloadFiles && (
                <DownloadModal
                    files={downloadFiles}
                    onClose={() => setDownloadFiles(null)}
                />
            )}
        </Box>
    );
}
